import { axiosDZAuthInstance } from '../axios'
import { TPromoRule, TPromoRuleCreateRequest, TPromoRuleCreateVersionRequest, TPromoRuleVersion } from '@/modules/PromoRules/types'
import { GetListParams } from 'react-admin'
import { paramsMapper, responseMapper } from '../helpers'
import { TMeta } from '@/types/data'

// Response type from server
interface TPromoRulesListResponse {
  data: TPromoRule[]
  meta: TMeta
}

export const getPromoRules = async (params?: {
  offset?: number
  limit?: number
  isActive?: boolean
  isLocked?: boolean
  name?: string
}): Promise<{ data: TPromoRule[]; total: number }> => {
  try {
    const { data } = await axiosDZAuthInstance.get<TPromoRulesListResponse>(
      '/dashboard/promo-rules',
      { params },
    )
    // Use responseMapper pattern for consistency
    return responseMapper(data)
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch promo rules')
  }
}

export const getActivePromoRules = async (): Promise<TPromoRule[]> => {
  try {
    const response = await getPromoRules({ isActive: true , limit: 1000}) // extend the limit to 1000000 to get all active promo rules for select 
    return response.data
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch active promo rules')
  }
}

export const getPromoRule = async (id: string): Promise<TPromoRule> => {
  try {
    const { data } = await axiosDZAuthInstance.get<TPromoRule | { data: TPromoRule }>(`/dashboard/promo-rules/${id}`)
    // Handle both wrapped and unwrapped response formats
    const promoRule = 'data' in data && data.data && typeof data.data === 'object' ? data.data : data as TPromoRule
    return promoRule
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error('Promo rule not found')
    }
    throw new Error(error.response?.data?.message || 'Failed to fetch promo rule')
  }
}

export const getPromoRuleVersions = async (id: string): Promise<TPromoRuleVersion[]> => {
  try {
    const { data } = await axiosDZAuthInstance.get<TPromoRuleVersion[] | { data: TPromoRuleVersion[] }>(`/dashboard/promo-rules/${id}/versions`)
    // Handle both wrapped and unwrapped response formats
    const versions = Array.isArray(data) ? data : (data.data || [])
    return versions
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch promo rule versions')
  }
}

export const createPromoRule = async (data: TPromoRuleCreateRequest): Promise<TPromoRule> => {
  try {
    const { data: response } = await axiosDZAuthInstance.post<TPromoRule>('/dashboard/promo-rules', data)
    return response
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Failed to create promo rule'
    throw new Error(errorMessage)
  }
}

export const createPromoRuleVersion = async (
  id: string,
  data: TPromoRuleCreateVersionRequest,
): Promise<TPromoRuleVersion> => {
  try {
    const { data: response } = await axiosDZAuthInstance.post<TPromoRuleVersion>(
      `/dashboard/promo-rules/${id}/versions`,
      data,
    )
    return response
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Failed to create promo rule version'
    throw new Error(errorMessage)
  }
}

export const updatePromoRule = async (id: string, data: Partial<TPromoRuleCreateRequest>): Promise<TPromoRule> => {
  try {
    const { data: response } = await axiosDZAuthInstance.put<TPromoRule>(`/dashboard/promo-rules/${id}`, data)
    return response
  } catch (error: any) {
    if (error.response?.status === 403) {
      throw new Error('Cannot edit locked promo rule version. Create a new version instead.')
    }
    const errorMessage = error.response?.data?.message || 'Failed to update promo rule'
    throw new Error(errorMessage)
  }
}

export const deletePromoRule = async (id: string): Promise<void> => {
  try {
    await axiosDZAuthInstance.delete(`/dashboard/promo-rules/${id}`)
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error('Promo rule not found')
    }
    
    // Handle specific error cases for locked rules and rules with dependencies
    if (error.response?.status === 400) {
      const errorData = error.response?.data
      const description = errorData?.description || errorData?.message
      
      // Check if it's a locked rule error
      if (description?.includes('locked')) {
        throw new Error('Cannot delete this promo rule because it is locked. Locked rules have been used and cannot be deleted.')
      }
      
      // Check if it's a dependency error
      if (description?.includes('referenced') || description?.includes('dependencies')) {
        const dependencies = errorData?.dependencies
        if (dependencies) {
          const parts: string[] = []
          if (dependencies.bulkPromoCodeCount > 0) {
            parts.push(`${dependencies.bulkPromoCodeCount} bulk campaign(s)`)
          }
          if (dependencies.promoCodeCount > 0) {
            parts.push(`${dependencies.promoCodeCount} promo code(s)`)
          }
          if (dependencies.orderCount > 0) {
            parts.push(`${dependencies.orderCount} order(s)`)
          }
          throw new Error(`Cannot delete this promo rule because it is referenced by ${parts.join(', ')}.`)
        }
        throw new Error(description)
      }
      
      throw new Error(description || 'Cannot delete this promo rule')
    }
    
    const errorMessage = error.response?.data?.message || error.response?.data?.description || 'Failed to delete promo rule'
    throw new Error(errorMessage)
  }
}
